import { UploadEvent, type UploadState, type UploadWorkerConfig } from "@/views/case-settings/webworkers/types";
import type { Ref } from "vue";
import { ScanUploadState } from "@/views/case-settings/upload/shared/ScanUploadState";
import { startStudyProcess } from "@/views/case-settings/api/createStudy";
import { useVersion } from "@/stores/version";
import { isUserFriendlyErrorMessage } from "@/api/http";
import { startHipSpineStudyProcess } from "@/views/case-settings/api/createHipSpineStudy";

/**
 * Starts the upload process using a web worker.
 * Handles progress tracking and completion callbacks.
 */
export async function startUpload(
    config: UploadWorkerConfig,
    state: Ref<UploadState>
): Promise<void> {
    state.value.hasUploaded = false;
    state.value.progress.error = null;
    state.value.progress.current = 0;
    state.value.progress.total = config.fileCount;

    const worker = new Worker(config.workerUrl, { type: 'module' });
    state.value.progress.worker = worker;

    worker.onmessage = async ({ data }: MessageEvent<UploadEvent>) => {
        if (data === UploadEvent.Next) {
            state.value.progress.current += 1;
        }
        if (data === UploadEvent.Completed) {
            const uploadType = config.workerMessage.uploadType;
            try {
                if (uploadType === 'xray-hip-spine') {
                    await startHipSpineStudyProcess(
                        config.caseId,
                        config.studyId, // studyId here represents the Hip Spine Study Id and is different from case study Id
                        useVersion().webComponentVersion
                    );
                } else {
                    await startStudyProcess(
                        config.caseId,
                        config.studyId,
                        useVersion().webComponentVersion
                    );
                }

                state.value.hasUploaded = true;
                state.value.state = ScanUploadState.Initial;

                await config.onComplete();
            } catch {
                state.value.state = ScanUploadState.Error;
                state.value.error = 'Could not start study processing. Upload again';
            }
        }
    };

    worker.onerror = (error) => {
        console.error(error);
        state.value.progress.error = 'Error uploading files, please contact support for assistance';
        state.value.state = ScanUploadState.Error;
    };

    worker.postMessage(config.workerMessage);
}

/**
 * Handles errors during the upload process.
 */
export function handleUploadError(error: any, state: Ref<UploadState>): void {
    state.value.state = ScanUploadState.Error;
    if (isUserFriendlyErrorMessage(error)) {
        state.value.error = error;
    } else {
        console.error(error);
        state.value.error = 'Error preparing files for upload';
    }
}

/**
 * Cancels an ongoing upload.
 */
export function cancelUpload(state: Ref<UploadState>): void {
    const isUploading = state.value.state === ScanUploadState.UploadingData;

    if (isUploading) {
        console.info('Cancelling files upload');
        if (state.value.progress.worker) {
            state.value.progress.worker.terminate();
        }
    }
    state.value.state = ScanUploadState.Initial;
}

/**
 * Creates the initial state for an upload store.
 */
export function makeInitialUploadState(): UploadState {
    return {
        state: ScanUploadState.Initial,
        error: '',
        hasUploaded: false,
        progress: {
            error: '',
            current: 0,
            total: null,
            worker: null,
        },
        patientIdentityCheck: null,
        patientComparison: null,
    };
}